import React, { useState, useEffect } from 'react';
import BurgerMenuIcon from './BurgerMenuIcon';
import { Box, Container, IconButton, Stack, Typography, CircularProgress, Button } from '@mui/material'; // Added Button
import { Link, useNavigate } from 'react-router-dom';
import { theme, themeColors } from '../styles/theme';
import Fonts from './Fonts';
import CloseIcon from "@mui/icons-material/Close";
import PubSearchGoogle, { GooglePubResult } from './PubSearchGoogle';
import { getPubByGooglePlaceId, fetchMinimalMeshes, getUnmoderatedPubs } from '../API';
import extractCity from '../utils/cityExtractor';
import axios from 'axios'; // Keep for create_pub if needed, or remove if API.ts handles all
import config from '../config';
import { useAuth } from '../AuthProvider';
import LoginModal from './LoginModal';
import ProfileModal from './ProfileModal'; // Import ProfileModal
import { trackHomePageExit, trackPubNotFound } from '../utils/analytics';
import suntapLogo from '../img/suntap_logo.svg';
import sunIcon from '../img/sun_icon.png';
import AccountCircleIcon from '@mui/icons-material/AccountCircle'; // For profile icon

interface TopNavProps {
    title: string; // title prop seems unused in the current TopNav rendering, consider removing if not needed
}

export const HEADER_HEIGHT = '10vh'; // Consider moving to a constants file if used elsewhere
const MENU_ITEM_STYLE = {
    marginTop: '12px',
    padding: '12px',
    textAlign: 'center',
    borderRadius: '40px',
    color: themeColors.darkBackground,
    fontFamily: "DM Sans, sans-serif",
    fontSize: '1.2rem',
    fontWeight: 600,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        color: themeColors.orange,
        transform: 'translateX(-4px)',
        // borderRadius: '40px', // Already set
    }
}

const TopNav = ({
    title, // title is passed but not used in JSX
}: TopNavProps) => {
    const navigate = useNavigate();
    const [burgerMenuOpen, setBurgerMenuOpen] = useState(false);
    
    const [searchError, setSearchError] = useState<string | React.ReactNode | null>(null);
    const [searchLoading, setSearchLoading] = useState(false); // Renamed to avoid conflict

    const { user, logout, loading: authLoading, isAnonymous, profile } = useAuth();
    const [loginModalOpen, setLoginModalOpen] = useState<boolean>(false);
    const [profileModalOpen, setProfileModalOpen] = useState<boolean>(false); // State for ProfileModal
    const [isStaff, setIsStaff] = useState(false);

    useEffect(() => {
        if (user && !isAnonymous && !authLoading) {
            const checkStaffStatus = async () => {
                try {
                    await getUnmoderatedPubs(1, 1);
                    setIsStaff(true);
                } catch (err) {
                    if (axios.isAxiosError(err) && (err.response?.status === 403 || err.response?.status === 401)) {
                        setIsStaff(false);
                    } else {
                        console.error("Error checking staff status:", err);
                        setIsStaff(false);
                    }
                }
            };
            checkStaffStatus();
        } else {
            setIsStaff(false);
        }
    }, [user, isAnonymous, authLoading]);

    const handleLogout = async () => {
        await logout();
        setBurgerMenuOpen(false); // Close menu on logout
    }

    const handleAuthAction = () => {
        setBurgerMenuOpen(false); // Close menu first
        if (user && !isAnonymous) { // If logged-in (not anonymous)
            setProfileModalOpen(true); // Open profile modal
        } else { // If anonymous or no user
            setLoginModalOpen(true); // Open login modal
        }
    };
    
    // Derived state for auth button text
    let authButtonText = 'LOGIN / SIGN UP';
    if (!authLoading) {
        if (user && !isAnonymous) {
            authButtonText = profile?.display_name || user.email || 'MY ACCOUNT';
        }
    }


    const handlePubSelect = async (selectedPub: GooglePubResult) => {
        try {
            setSearchLoading(true);
            setSearchError(null);

            if (!selectedPub.latitude || !selectedPub.longitude ||
                isNaN(selectedPub.latitude) || isNaN(selectedPub.longitude) ||
                Math.abs(selectedPub.latitude) > 90 || Math.abs(selectedPub.longitude) > 180) {
                setSearchError("Invalid location coordinates. Please try another pub.");
                setSearchLoading(false);
                return;
            }

            const existingPubResponse = await getPubByGooglePlaceId(selectedPub.google_place_id);
            
            let pubToNavigate;

            if (existingPubResponse.data && existingPubResponse.data.exists && existingPubResponse.data.pub) {
                console.log('Pub exists in database:', existingPubResponse.data.pub);
                const existingPub = existingPubResponse.data.pub;
                const city = existingPub.city || extractCity({
                    name: selectedPub.name,
                    formatted_address: selectedPub.formatted_address || selectedPub.address || '',
                    address_components: [] 
                });
                 pubToNavigate = {
                    google_place_id: existingPub.google_place_id,
                    name: existingPub.name || selectedPub.name,
                    latitude: typeof existingPub.latitude === 'number' ? existingPub.latitude : selectedPub.latitude,
                    longitude: typeof existingPub.longitude === 'number' ? existingPub.longitude : selectedPub.longitude,
                    address: existingPub.address || selectedPub.formatted_address || selectedPub.address || '',
                    garden_drawn: existingPub.garden_drawn || false,
                    garden_moderated: existingPub.garden_moderated || false,
                    has_no_garden: existingPub.has_no_garden || false,
                    city: city,
                    distance: existingPub.distance || 0,
                    uid: existingPub.google_place_id, // Assuming uid is google_place_id for PubObject consistency
                };

            } else {
                console.log('Pub not found in database, checking if location is supported');
                await fetchMinimalMeshes(selectedPub.latitude, selectedPub.longitude, 11, true);
                console.log('Location is supported, proceeding to add pub');

                const city = extractCity({
                    name: selectedPub.name,
                    formatted_address: selectedPub.formatted_address || selectedPub.address || '',
                    address_components: []
                });
                const latitude = Number(selectedPub.latitude);
                const longitude = Number(selectedPub.longitude);

                const createPubPayload = {
                    pub: {
                        // name: selectedPub.name, // Name is deprecated in Pub model
                        // address: selectedPub.formatted_address || selectedPub.address || '', // Address is deprecated
                        // latitude: latitude, // Latitude is deprecated
                        // longitude: longitude, // Longitude is deprecated
                        garden_drawn: false,
                        garden_moderated: false,
                        has_no_garden: false,
                        city: city,
                        google_place_id: selectedPub.google_place_id
                    }
                };
                
                // Using api.post from API.ts which includes the interceptor
                // Assuming API.ts has createPub function like:
                // export const createPub = (payload) => api.post('/create_pub/', payload);
                // If not, you can use:
                // const createResponse = await axios.post(`${config.apiBaseUrl}/create_pub/`, createPubPayload, { headers: { Authorization: `Bearer ${session?.access_token}` }});
                // For now, let's assume createPub exists in API.ts and uses the configured `api` instance
                const { createPub } = await import('../API'); // Dynamically import to use `api` instance
                const createResponse = await createPub(createPubPayload);


                if (createResponse.status === 201 && createResponse.data.google_place_id) {
                    console.log(`Successfully added pub with GID: ${createResponse.data.google_place_id}`);
                     pubToNavigate = {
                        name: selectedPub.name,
                        google_place_id: createResponse.data.google_place_id,
                        address: selectedPub.formatted_address || selectedPub.address || '',
                        latitude: latitude,
                        longitude: longitude,
                        garden_drawn: false,
                        garden_moderated: false,
                        has_no_garden: false,
                        city: city,
                        distance: 0,
                        uid: createResponse.data.google_place_id, // For PubObject consistency
                    };
                } else {
                    throw new Error(createResponse.data?.message || createResponse.data?.error || 'Failed to create pub');
                }
            }

            if (pubToNavigate) {
                setSearchLoading(false);
                setBurgerMenuOpen(false);
                trackHomePageExit(`pub_${pubToNavigate.google_place_id}`);
                navigate(`/pub/${pubToNavigate.google_place_id}`, {
                    state: { pubToView: pubToNavigate }
                });
            }

        } catch (checkError: any) {
            console.error('Error checking or creating pub:', checkError);
            setSearchLoading(false);
            if (checkError.type === 'flat_mesh') {
                setSearchError(checkError.message || "This location isn't supported for 3D calculations.");
            } else if (checkError.response?.data?.error && /flat.*triangles/.test(checkError.response.data.error)) {
                setSearchError("We're sorry, this pub isn't currently supported. Please try another.");
            } else if (checkError.response?.status === 404 && checkError.config?.url?.includes('get_minimal_meshes')) {
                setSearchError("3D data not found for this area. Please try another pub.");
            } else {
                const pubName = selectedPub.name || "pub";
                trackPubNotFound(pubName);
                setSearchError(checkError.message || `Could not find or add "${pubName}".`);
            }
        }
    };

    return (
        <Box
            sx={{
                top: 0,
                width: '100%',
                color: '#FBE858',
                height: HEADER_HEIGHT,
                position: 'relative', // Changed from 'fixed' to allow content below
            }}
        >
        <Fonts />
        <Box
            sx={{
                top: '0', // Corrected
                marginTop: '10px',
                width: '90%',
                height: HEADER_HEIGHT,
                background: theme.palette.background.paper,
                boxShadow: '0 1px 6px rgba(0, 0, 0, 0.1)',
                position: 'fixed', // This keeps the nav fixed
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                borderRadius: '30px',
                left: '50%', // Center the nav
                transform: 'translateX(-50%)' // Center the nav
            }}
        >
            <Container maxWidth="lg" sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                height: '100%',
                position: 'relative' 
            }}>
                <Link to="/" style={{ textDecoration: 'none' }} onClick={() => setBurgerMenuOpen(false)}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%', // Fill height of parent
                            // lineHeight: 1, // Removed, not needed with flex centering
                        }}
                    >
                        <img
                            src={suntapLogo}
                            alt="Suntap Logo"
                            style={{
                                height: '50px', // Adjust as needed
                                // width: 'auto' // Maintain aspect ratio
                            }}
                        />
                    </Box>
                </Link>
                <Stack 
                    direction={'row'} 
                    sx={{ 
                        alignItems: 'center',
                        height: '100%'
                    }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        mr: 1 // Add some margin if needed
                    }}>
                        <img
                            src={sunIcon}
                            alt="Sun Icon"
                            style={{
                                height: '35px',
                            }}
                        />
                    </Box>
                    <IconButton
                        edge="end"
                        color="inherit"
                        aria-label="menu"
                        onClick={() => {
                            setBurgerMenuOpen(!burgerMenuOpen)
                        }}
                         sx={{ color: themeColors.orange }} // Ensure icon has a visible color
                    >
                        <BurgerMenuIcon />
                    </IconButton>
                </Stack>
            </Container>
        </Box>

        {burgerMenuOpen && (
        <Box
            className="menu-bar" // Keep class if used for CSS transitions
            sx={{
                position: 'fixed',
                top: `calc(${HEADER_HEIGHT} + 20px)`, // Position below the fixed header + margin
                left: '50%',
                transform: 'translateX(-50%)',
                height: `calc(100vh - ${HEADER_HEIGHT} - 30px)`, // Adjust height
                width: '90%',
                maxWidth: '600px', // Max width for larger screens
                backgroundColor: themeColors.lightBlue, // Or a slightly transparent version
                // backdropFilter: 'blur(5px)', // Optional: if you want blur
                zIndex: 1100, // Higher than fixed nav
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                padding: '24px', // Uniform padding
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                borderRadius: '20px',
                overflowY: 'auto', // Allow scrolling if content overflows
                transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out', // Smooth transition
                // transform: burgerMenuOpen ? 'translateY(0)' : 'translateY(-100%)', // Slide in/out
                // opacity: burgerMenuOpen ? 1 : 0,
            }}
        >
            <IconButton
                onClick={() => setBurgerMenuOpen(false)}
                sx={{
                    position: 'absolute',
                    top: '10px', // Adjust positioning
                    right: '10px',
                    color: themeColors.darkBackground,
                }}
            >
                <CloseIcon />
            </IconButton>
            
            <Box sx={{ width: '100%', mb: 2, mt: 4 }}> {/* Adjusted mt for close button */}
                <Typography 
                    variant="h6" 
                    sx={{ 
                        textAlign: 'center', 
                        color: themeColors.darkBackground,
                        fontFamily: "DM Sans, sans-serif",
                        fontWeight: 600,
                        mb: 1 // Reduced margin
                    }}
                >
                    SEARCH FOR A PUB
                </Typography>
                <Box sx={{ position: 'relative', width: '100%' }}>
                    <PubSearchGoogle
                        onPubSelect={handlePubSelect}
                        placeholder="E.g., The Red Lion, Covent Garden"
                        onError={(err) => setSearchError(typeof err === 'string' ? err : 'Search error')}
                        onLoading={setSearchLoading}
                        containerSx={{ width: '100%' }}
                        inputSx={{
                            borderRadius: '40px',
                            fontFamily: "DM Sans, sans-serif",
                            backgroundColor: theme.palette.background.default, // Lighter background for input
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: themeColors.orange, // Border color
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: themeColors.blue, // Hover border color
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: themeColors.blue, // Focused border color
                            },
                            '.MuiOutlinedInput-input': {
                                fontFamily: "DM Sans, sans-serif",
                                color: themeColors.darkBackground,
                            },
                        }}
                    />
                    {searchLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                            <CircularProgress size={24} sx={{color: themeColors.orange}}/>
                        </Box>
                    )}
                    {searchError && (
                        <Typography 
                            color="error" 
                            sx={{ mt: 1, fontSize: '0.8rem', textAlign: 'center' }}
                        >
                            {searchError}
                        </Typography>
                    )}
                </Box>
            </Box>
            
            <Link to="/" style={{ textDecoration: 'none', width: '100%' }} onClick={() => setBurgerMenuOpen(false)}>
                <Typography variant="h5" sx={MENU_ITEM_STYLE}>HOME</Typography>
            </Link>
            <Link to="/search-area" style={{ textDecoration: 'none', width: '100%' }} onClick={() => setBurgerMenuOpen(false)}>
                <Typography variant="h5" sx={MENU_ITEM_STYLE}>SEARCH AREA</Typography>
            </Link>
            <Link to="/pubs" style={{ textDecoration: 'none', width: '100%' }} onClick={() => setBurgerMenuOpen(false)}>
                <Typography variant="h5" sx={MENU_ITEM_STYLE}>PUBS NEAR YOU</Typography>
            </Link>
            <Link to="/about" style={{ textDecoration: 'none', width: '100%' }} onClick={() => setBurgerMenuOpen(false)}>
                <Typography variant="h5" sx={MENU_ITEM_STYLE}>ABOUT</Typography>
            </Link>
            <Link to="/submit" style={{ textDecoration: 'none', width: '100%' }} onClick={() => setBurgerMenuOpen(false)}>
                <Typography variant="h5" sx={MENU_ITEM_STYLE}>SUBMIT GARDEN</Typography>
            </Link>
            
            {isStaff && (
                <Link to="/moderate" style={{ textDecoration: 'none', width: '100%' }} onClick={() => setBurgerMenuOpen(false)}>
                    <Typography variant="h5" sx={MENU_ITEM_STYLE}>MODERATE</Typography>
                </Link>
            )}
            
            {/* Auth Action Button */}
            <Box 
                onClick={handleAuthAction} 
                sx={{ ...MENU_ITEM_STYLE, width: '100%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
                {authLoading ? (
                    <CircularProgress size={20} sx={{color: themeColors.darkBackground}} />
                ) : (
                    <>
                        {user && !isAnonymous && <AccountCircleIcon sx={{ mr: 1 }} />}
                        <Typography variant="h5" component="span" sx={{fontSize: '1.2rem', fontWeight: 600}}>
                           {authButtonText}
                        </Typography>
                    </>
                )}
            </Box>
            {/* Logout button specifically for logged-in users, if preferred over combined button */}
            {user && !isAnonymous && !authLoading && (
                 <Box 
                    onClick={handleLogout} 
                    sx={{ ...MENU_ITEM_STYLE, width: '100%', cursor: 'pointer', color: themeColors.orange, '&:hover': { color: themeColors.darkBackground, transform: 'translateX(-4px)'} }}
                >
                     <Typography variant="h5" component="span" sx={{fontSize: '1.2rem', fontWeight: 600}}>
                        LOGOUT
                    </Typography>
                </Box>
            )}
        </Box>
        )}

        <LoginModal 
            open={loginModalOpen}
            onClose={() => setLoginModalOpen(false)}
        />
        <ProfileModal
            open={profileModalOpen}
            onClose={() => setProfileModalOpen(false)}
        />
        </Box>
    )
}

export default TopNav;